/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { baseStyles, i18n, I18nService, property, event } from '@cds/core/internal';
import { GridColumnSizeController } from './grid-column-size.controller.js';
import { GridColumnPositionController } from './grid-column-position.controller.js';
import styles from './grid-column.element.scss';
/**
 * Grid Column
 *
 * ```typescript
 * import '@cds/core/grid/register.js';
 * ```
 *
 * @internal
 * @element cds-grid-column
 * @event resizeChange - notifies when column has been resized
 * @cssprop --min-height
 * @cssprop --min-width
 * @cssprop --justify-content
 * @cssprop --padding-block
 * @cssprop --padding-inline-start
 * @cssprop --padding-inline-end
 */
export class CdsGridColumn extends LitElement {
    constructor() {
        super(...arguments);
        this.i18n = I18nService.keys.grid;
        this.resizable = false;
        this.type = '';
        this.position = '';
        this.gridColumnSizeController = new GridColumnSizeController(this);
        this.gridColumnPositionController = new GridColumnPositionController(this);
    }
    get grid() {
        return this.parentElement;
    }
    render() {
        return html `
      <div class="private-host" focusable>
        <slot>
          ${this.type === 'action'
            ? html `<span cds-layout="display:screen-reader-only">${this.i18n.action}</span>`
            : ''}
        </slot>
        ${this.resizable
            ? html `<cds-internal-split-handle
              @cdsTouchStart=${() => (this.grid.scrollLock = true)}
              @cdsTouchEnd=${() => (this.grid.scrollLock = false)}
            >
              <input
                type="range"
                step="10"
                .ariaLabel=${this.i18n.resizeColumn}
                @change=${(e) => this.resizeChange.emit(e.target.valueAsNumber)}
              />
            </cds-internal-split-handle>`
            : html `<cds-internal-split-handle readonly></cds-internal-split-handle>`}
        <div class="line"></div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.slot = 'columns';
    }
}
CdsGridColumn.styles = [baseStyles, styles];
__decorate([
    i18n()
], CdsGridColumn.prototype, "i18n", void 0);
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "width", void 0);
__decorate([
    property({ type: Boolean })
], CdsGridColumn.prototype, "resizable", void 0);
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "type", void 0);
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "position", void 0);
__decorate([
    event()
], CdsGridColumn.prototype, "resizeChange", void 0);
